import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setTokens, clearSession, STORAGE } from '@general/ui-kit'

import { authApi } from './api'
import { runBeforeLogout, watchSessionEndedElsewhere } from './sessionEvents.mjs'

/**
 * Session state.
 *
 * The login is a small state machine, not a single call — the backend can answer
 * three different ways and the original codebase only handled one of them:
 *
 *   password ok, no MFA        -> tokens, done
 *   password ok, MFA on        -> { requiresMfa, mfaToken }      -> code step
 *   password ok, MFA enforced  -> { requiresMfaSetup, setupToken } -> enrol step
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Intermediate login state
  const stage = ref('credentials')   // credentials | mfa | enrol
  const mfaToken = ref(null)

  const isAuthenticated = computed(() => Boolean(user.value && localStorage.getItem(STORAGE.access)))
  const role = computed(() => user.value?.role ?? null)
  const permissions = computed(() => user.value?.permissions ?? [])

  // Rehydrate so a page refresh does not bounce the user to the login screen.
  const stored = localStorage.getItem(STORAGE.user)
  if (stored) {
    try { user.value = JSON.parse(stored) } catch { clearSession() }
  }

  // The API client clears the tokens and fires this when a refresh fails; the
  // store only has to forget the user. Redirecting is the shell's job.
  window.addEventListener('general:unauthorized', () => { user.value = null })

  // Otra pestaña cerró la sesión (o la perdió): esta también, y sin enseñar un
  // minuto más lo que ya tenía cargado. `elsewhere` es lo que le dice al
  // armazón que nadie en ESTA pestaña la ha llevado a la entrada todavía.
  watchSessionEndedElsewhere(window, STORAGE.refresh, () => {
    if (!user.value) return
    user.value = null
    window.dispatchEvent(new CustomEvent('general:session-ended', { detail: { elsewhere: true } }))
  })

  /** True for a superadmin, for '*', or for the exact permission. */
  function can(permission) {
    if (!user.value) return false
    if (role.value === 'superadmin') return true
    return permissions.value.includes('*') || permissions.value.includes(permission)
  }

  function setSession(data) {
    setTokens(data)
    user.value = data.user
    localStorage.setItem(STORAGE.user, JSON.stringify(data.user))
    stage.value = 'credentials'
    mfaToken.value = null
  }

  async function login(email, password) {
    loading.value = true
    error.value = null
    try {
      const data = await authApi.login(email, password)

      if (data.requiresMfa) {
        mfaToken.value = data.mfaToken
        stage.value = 'mfa'
        return { next: 'mfa' }
      }

      if (data.requiresMfaSetup) {
        // The setup token only unlocks the MFA endpoints; storing it as the
        // access token is what lets the enrolment screen call them.
        setTokens({ accessToken: data.setupToken })
        stage.value = 'enrol'
        return { next: 'enrol' }
      }

      setSession(data)
      return {
        next: 'app',
        mfaSetupRecommended: data.mfaSetupRecommended,
        // An administrator who typed this password knows it, so it is good for
        // exactly one sign-in. The backend has been saying so since the
        // beginning and nobody was reading it, which made "good for one sign-in"
        // mean "good for ever".
        mustChangePassword: data.mustChangePassword,
      }
    } catch (err) {
      error.value = err
      return { next: 'error' }
    } finally {
      loading.value = false
    }
  }

  async function submitMfaCode(code) {
    loading.value = true
    error.value = null
    try {
      const data = await authApi.validateMfa(mfaToken.value, code)
      setSession(data)
      return { next: 'app', mustChangePassword: data.mustChangePassword }
    } catch (err) {
      error.value = err
      return { next: 'error' }
    } finally {
      loading.value = false
    }
  }

  /** After enrolling from the login screen, sign in again to get real tokens. */
  function finishEnrolment() {
    clearSession()
    stage.value = 'credentials'
    user.value = null
  }

  async function logout() {
    // Con el token todavía vivo: la baja del push en el servidor la necesita.
    await runBeforeLogout()
    try {
      await authApi.logout(localStorage.getItem(STORAGE.refresh))
    } catch { /* the session is going away either way */ }
    clearSession()
    user.value = null
    // Other stores listen so no data from this user is left in memory on a
    // shared device.
    window.dispatchEvent(new Event('general:session-ended'))
  }

  /** Reloads the profile, e.g. after an admin changes your permissions. */
  async function refreshProfile() {
    try {
      const { user: fresh } = await authApi.me()
      user.value = fresh
      localStorage.setItem(STORAGE.user, JSON.stringify(fresh))
    } catch { /* keep the cached profile */ }
  }

  /**
   * Saves some of the person's own preferences to their account (the theme,
   * the language) and mirrors them into the cached profile, so the next
   * reload does not bring back the old value before `/me` answers.
   *
   * Never throws: the choice is already applied in this browser, and a failed
   * save only means the other devices learn it later.
   */
  async function savePreferences(changes) {
    if (!isAuthenticated.value) return
    try {
      const { user: fresh } = await authApi.updateProfile(changes)
      if (!fresh || !user.value) return
      // Only the fields that were saved. The answer carries the ACCOUNT's
      // permissions, and a scoped session (a demo opened for two modules) holds
      // fewer: copying the whole profile would widen its menu until the reload.
      const merged = { ...user.value }
      for (const key of Object.keys(changes)) merged[key] = fresh[key]
      user.value = merged
      localStorage.setItem(STORAGE.user, JSON.stringify(merged))
    } catch { /* kept locally; the account catches up on the next choice */ }
  }

  function resetStage() {
    stage.value = 'credentials'
    mfaToken.value = null
    error.value = null
  }

  return {
    user, loading, error, stage, mfaToken,
    isAuthenticated, role, permissions,
    can, login, submitMfaCode, finishEnrolment, logout, refreshProfile, resetStage, setSession,
    savePreferences,
  }
})
